import React, {useEffect, useState} from 'react';
import AnalyticsUI from '../view/AnalyticsUI';
import {inject, observer} from 'mobx-react';
import AnalyticsStore from '../store/AnalyticsStore';
import {useNavigate, useParams} from 'react-router-dom';
import {downloadCSV} from '../../../../utils/downloadCsv';
import {analyticsText} from '../../analyticsText';
import {hasValue} from '../../../../utils/typecheck';

interface Props {
  analyticsStore?: AnalyticsStore;
}

const AnalyticsContainer = inject('analyticsStore')(
  observer(({analyticsStore}: Props) => {
    const [search, setSearch] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    const [orderAsc, setOrderAsc] = useState(true);
    const {organizationId} = useParams();
    const navigate = useNavigate();

    useEffect(() => {
      if (organizationId) {
        analyticsStore!.getCoursesList(organizationId);
        analyticsStore!.getLearnersList(organizationId, search, orderAsc);
      }
    }, []);

    useEffect(() => {
      if (selectedCourse) {
        navigate(`course/${selectedCourse}/learners`);
        setSelectedCourse('');
      }
    }, [selectedCourse]);

    useEffect(() => {
      if (organizationId) {
        analyticsStore!.getLearnersList(organizationId, search, orderAsc);
      }
    }, [search, orderAsc]);

    const onExport = async () => {
      await analyticsStore!.loadAllLearners(organizationId!, search, orderAsc);
      downloadCSV(
        [
          [
            analyticsText.learner,
            analyticsText.avgScore,
            ...analyticsStore!.coursesList.map(c => c.name),
          ],
          [
            analyticsText.overallScore,
            hasValue(analyticsStore!.coursesAnalytics!.globalAvgScore)
              ? Math.round(analyticsStore!.coursesAnalytics!.globalAvgScore) +
                '%'
              : '-',
            ...analyticsStore!.coursesList.map(c =>
              hasValue(c.avgScore) ? Math.round(c.avgScore || 0) + '%' : '-',
            ),
          ],
          ...analyticsStore!.learnersList.map(l => [
            l.name,
            hasValue(l.avgScore) ? Math.round(l.avgScore || 0) + '%' : '-',
            ...analyticsStore!.coursesList.map(c => {
              if (c.isGradable) {
                if (
                  analyticsStore!.gradesMap[l.learnerId] &&
                  hasValue(
                    analyticsStore!.gradesMap[l.learnerId].find(
                      g => g.courseId === c.id,
                    )?.grade,
                  )
                ) {
                  return (
                    Math.round(
                      analyticsStore!.gradesMap[l.learnerId].find(
                        g => g.courseId === c.id,
                      )?.grade || 0,
                    ) + '%'
                  );
                } else {
                  return '-';
                }
              } else if (
                analyticsStore!.gradesMap[l.learnerId] &&
                analyticsStore!.gradesMap[l.learnerId].find(
                  g => g.courseId === c.id,
                ) !== undefined
              ) {
                return 'Completed';
              }
            }),
          ]),
        ],
        'courses_overview.csv',
      );
    };

    const loadNextPage = async () => {
      if (organizationId) {
        await analyticsStore!.loadNextLearnersPage(
          organizationId,
          search,
          orderAsc,
        );
      }
    };

    return (
      <AnalyticsUI
        search={search}
        setSearch={setSearch}
        learners={analyticsStore!.learnersList}
        courses={analyticsStore!.coursesList}
        grades={analyticsStore!.gradesMap}
        loadNextPage={loadNextPage}
        coursesAnalytics={analyticsStore!.coursesAnalytics}
        isLoading={analyticsStore!.isLoading}
        orderAsc={orderAsc}
        setOrderAsc={setOrderAsc}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        onExport={onExport}
      />
    );
  }),
);

export default AnalyticsContainer;
